import { useCallback, useEffect, useState } from "react";
import { Telescope, Plus, X, RefreshCw, Trash2, ExternalLink, Lightbulb, Loader2, Sparkles } from "lucide-react";
import { Card } from "@/components/common/PageHeader";
import { Loading, Stat } from "@/pages/_shared";
import { toast } from "sonner";
import api from "@/lib/api";

const IDEA_CLS = {
  proposed: "bg-slate-100 text-slate-600", approved: "bg-emerald-50 text-emerald-600",
  building: "bg-blue-50 text-[#1a5fd0]", dismissed: "bg-red-50 text-red-500",
};

export default function CompetitorWatch() {
  const [data, setData] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", url: "", notes: "" });
  const [scanningId, setScanningId] = useState(null);
  const [scanningAll, setScanningAll] = useState(false);
  const load = useCallback(() => { api.get("/competitors/overview").then(({ data }) => setData(data)); }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading />;
  const { connected, competitors, ideas, stats, idea_statuses } = data;

  const add = async () => {
    if (!form.name.trim()) return toast.error("Competitor name is required.");
    try { await api.post("/competitors", form); toast.success("Competitor added"); setAddOpen(false); setForm({ name: "", url: "", notes: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add competitor."); }
  };
  const del = async (id) => { try { await api.delete(`/competitors/${id}`); load(); } catch { toast.error("Could not delete."); } };
  const scan = async (id) => {
    setScanningId(id);
    try { const { data } = await api.post(`/competitors/${id}/scan`); toast.success(`Scan complete · ${data.ideas_created} new idea${data.ideas_created === 1 ? "" : "s"}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Scan failed."); } finally { setScanningId(null); }
  };
  const scanAll = async () => {
    setScanningAll(true);
    try { const { data } = await api.post("/competitors/scan-all"); toast.success(`Scanned ${data.scanned} · ${data.ideas_created} new ideas`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Scan failed."); } finally { setScanningAll(false); }
  };
  const setIdea = async (id, status) => { try { await api.patch(`/competitors/ideas/${id}`, { status }); load(); } catch { toast.error("Could not update."); } };

  const activeIdeas = ideas.filter((i) => i.status !== "dismissed");

  return (
    <div className="animate-fade-up space-y-6" data-testid="competitor-watch-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-head text-2xl font-extrabold tracking-tight text-slate-900"><Telescope size={22} className="text-[#b0862f]" /> Competitor Watch</h1>
          <p className="mt-1 text-[13px] text-slate-500">Ivy scans your competitors and turns feature gaps into product ideas. {connected ? <span className="font-semibold text-emerald-600">Live</span> : <span className="font-semibold text-amber-600">Not connected</span>}</p>
        </div>
        <button onClick={scanAll} disabled={scanningAll || !competitors.length} data-testid="scan-all-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-[#080d1a] px-4 py-2.5 font-head text-[13px] font-bold text-white hover:bg-[#1a5fd0] disabled:opacity-60">
          <RefreshCw size={15} className={scanningAll ? "animate-spin" : ""} /> {scanningAll ? "Scanning…" : "Scan all now"}
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <Stat label="Competitors" value={stats.competitors} tone="slate" />
        <Stat label="Product ideas" value={stats.ideas} tone="blue" />
        <Stat label="Approved" value={stats.approved} tone="green" />
        <Stat label="Building" value={stats.building} tone="blue" />
      </div>

      {/* Competitors */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <span className="font-mono text-[10px] font-bold uppercase tracking-wider text-slate-400">Tracked competitors</span>
          <button onClick={() => setAddOpen(true)} data-testid="add-competitor-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-[#1a5fd0] px-3 py-1.5 text-[12px] font-bold text-white hover:bg-[#1550b8]"><Plus size={14} /> Add competitor</button>
        </div>
        {competitors.length === 0 ? (
          <Card className="p-8 text-center text-[13px] text-slate-400">No competitors yet. Add one and let Ivy scan it.</Card>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {competitors.map((c) => (
              <Card key={c.id} className="p-4" data-testid="competitor-card">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-head text-[15px] font-bold text-slate-900">{c.name}</div>
                    {c.url && <a href={c.url.startsWith("http") ? c.url : `https://${c.url}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[12px] text-[#1a5fd0] hover:underline">{c.url} <ExternalLink size={11} /></a>}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button onClick={() => scan(c.id)} disabled={scanningId === c.id || !connected} data-testid="scan-competitor-btn" className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-200 disabled:opacity-50">
                      {scanningId === c.id ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Scan
                    </button>
                    <button onClick={() => del(c.id)} className="text-slate-300 hover:text-red-500"><Trash2 size={15} /></button>
                  </div>
                </div>
                {c.summary && <p className="mt-2 rounded-lg bg-slate-50 p-2.5 text-[12px] leading-relaxed text-slate-600">{c.summary}</p>}
                {(c.pricing || c.suggested_price) && (
                  <div className="mt-2 grid gap-1.5 sm:grid-cols-2" data-testid="competitor-pricing">
                    {c.pricing && <div className="rounded-lg border border-slate-100 p-2"><div className="font-mono text-[9px] uppercase tracking-wider text-slate-400">Their pricing</div><div className="text-[12px] text-slate-700">{c.pricing}</div></div>}
                    {c.suggested_price && <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 p-2"><div className="font-mono text-[9px] uppercase tracking-wider text-emerald-600">Suggested MMT price</div><div className="text-[12px] font-semibold text-emerald-700">{c.suggested_price}</div></div>}
                  </div>
                )}
                <div className="mt-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-400">{c.last_scanned ? `Scanned ${new Date(c.last_scanned).toLocaleString()}` : "Not scanned yet"}</div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Product ideas */}
      <div>
        <div className="mb-2 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-400">Product ideas from feature gaps</div>
        {activeIdeas.length === 0 ? (
          <Card className="p-8 text-center text-[13px] text-slate-400">No ideas yet — scan a competitor to generate product ideas.</Card>
        ) : (
          <div className="space-y-2">
            {activeIdeas.map((i) => (
              <Card key={i.id} className="flex items-start justify-between gap-3 p-4" data-testid="idea-card">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#b0862f]/10 text-[#b0862f]"><Lightbulb size={16} /></span>
                  <div>
                    <div className="font-head text-[14px] font-bold text-slate-900">{i.title}</div>
                    {i.rationale && <div className="mt-0.5 text-[12px] text-slate-500">{i.rationale}</div>}
                    <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-slate-400">vs {i.competitor_name}</div>
                  </div>
                </div>
                <select value={i.status} onChange={(e) => setIdea(i.id, e.target.value)} data-testid="idea-status-select"
                  className={`shrink-0 rounded-lg border-0 px-2.5 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider outline-none ${IDEA_CLS[i.status]}`}>
                  {idea_statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Card>
            ))}
          </div>
        )}
      </div>

      {addOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="add-competitor-modal">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setAddOpen(false)} />
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between"><h3 className="font-head text-lg font-extrabold text-slate-900">Add competitor</h3><button onClick={() => setAddOpen(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button></div>
            <div className="space-y-3">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="competitor-name" placeholder="Competitor name" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[14px] outline-none focus:border-[#1a5fd0]" />
              <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="Website (optional)" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-[#1a5fd0]" />
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Notes (optional)" className="w-full rounded-lg border border-slate-200 p-3 text-[13px] outline-none focus:border-[#1a5fd0]" />
            </div>
            <button onClick={add} data-testid="save-competitor-btn" className="mt-4 w-full rounded-lg bg-[#1a5fd0] py-2.5 text-[14px] font-bold text-white hover:bg-[#1550b8]">Add competitor</button>
          </div>
        </div>
      )}
    </div>
  );
}
